import { Dispatch, useState, useEffect } from 'react';
import Slider, { SliderThumb } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { THEME } from 'src/styles/theme';
import { currencyFormat, isNotInvalid } from 'src/common/utils/functions';
import { ErrorObject } from 'src/containers/client-assessment/types';

interface AirbnbThumbComponentProps extends React.HTMLAttributes<unknown> {}

function AirbnbThumbComponent(props: AirbnbThumbComponentProps) {
  const { children, ...other } = props;
  return (
    <>
      <SliderThumb {...other}>{children}</SliderThumb>
    </>
  );
}

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  '& span': {
    font: '500 1.25rem/1.5rem Inter,sans-serif',
    color: '#101828',
  },
}));

interface StyledSliderProps {
  isDiscreteSlider?: boolean;
  isInverted?: boolean;
}

const StyledSlider = styled(Slider, {
  shouldForwardProp: props =>
    props !== 'isDiscreteSlider' && props !== 'isInverted',
})<StyledSliderProps>(
  ({ theme, isDiscreteSlider = false, isInverted = false }) => ({
    // color: theme.palette.primary.main,
    height: 8,
    borderRadius: 4,
    padding: '15px 0',
    width: '80%',
    '& .MuiSlider-thumb': {
      height: 24,
      width: 24,
      borderRadius: '24px',
      backgroundColor: '#101828',
      '&:focus, &:hover, &.Mui-active': {
        boxShadow:
          '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)',
        // Reset on touch devices, it doesn't add specificity
      },
      '& .airbnb-bar': {
        height: 15,
        width: 2,
        backgroundColor: '#fff',
        marginLeft: 1.5,
        marginRight: 1.5,
      },
    },
    '& .MuiSlider-valueLabel': {
      fontSize: 12,
      fontWeight: 'normal',
      top: -6,
      backgroundColor: 'unset',
      // color: theme.palette.text.primary,
      '&:before': {
        display: 'none',
      },
      ['@media (max-width:450px)']: {
        top: '200%',
        '& .MuiSlider-valueLabelLabel': {
          fontSize: 24,
        },
      },
      ['@media (max-height:480px)']: {
        top: '180%',
      },
    },
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: isDiscreteSlider
        ? 'transparent'
        : isInverted
        ? '#E4E7EC'
        : THEME.main.primary,
    },
    '& .MuiSlider-rail': {
      backgroundColor: isInverted ? THEME.main.primary : '#E4E7EC',
      backgroundImage: isDiscreteSlider
        ? 'linear-gradient(90deg, #FF8664 0%, #FCFCFD 50%, #4918FB 100%)'
        : '',
      opacity: isDiscreteSlider ? 1 : isInverted ? 1 : 0.5,
    },
    '& .MuiSlider-markLabel': {
      color: ' #101828',
      marginTop: '1rem',
      font: '500 1rem/1.1875rem Inter,sans-serif',
    },
    '& .MuiSlider-mark': {
      backgroundColor: THEME.main.primary,
      height: 0,
      width: 0,
      '&.MuiSlider-markActive': {
        opacity: 1,
        backgroundColor: 'currentColor',
      },
    },
  }),
);

const DynamicText = styled('p')(() => ({
  color: THEME.grey.dark,
}));

interface Props {
  children: React.ReactElement;
  value: number;
}

function ValueLabelComponent(props: Props) {
  const { children, value } = props;

  return (
    <Tooltip enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
}

const getFormattedValue = (key: string, value: number) => {
  switch (key) {
    case 'percentage_loss_to_gain':
      return value / 100;
    case 'philanthropic_causes_annual_percentage':
      return value / 100;
    case 'percentage_bet_annual_income':
      return value / 100;
    default:
      return value;
  }
};

const getOriginalValue = (key: string, value: number) => {
  switch (key) {
    case 'percentage_loss_to_gain':
      return value * 100;
    case 'philanthropic_causes_annual_percentage':
      return value * 100;
    case 'percentage_bet_annual_income':
      return value * 100;
    default:
      return value;
  }
};

const getDiscreteSteps = (min: number, max: number, marks: number[]) => {
  if (min === -1 && max === 1) return 0.5;
  if (max === 1) {
    const diff = marks.length > 0 ? Math.abs(marks[0] - marks[1]) : 1;
    return diff;
  }
  return 1;
};

const renderLabel = (key: string, value: number) => {
  switch (key) {
    default:
      return `${value}%`;
  }
};

type MainProps = {
  selectedQuestionId: string;
  questionnaireValue: any;
  questions: any[];
  setQuestionnaireValue: Dispatch<any>;
  metadata?: any;
  attributeKey: string;
  error: ErrorObject;
  setError: Dispatch<ErrorObject>;
};

function MainSlider({
  selectedQuestionId,
  questionnaireValue,
  setQuestionnaireValue,
  metadata,
  attributeKey,
  error,
  setError,
  questions,
}: MainProps) {
  const isInvestmentAmountDecline =
    attributeKey === 'investment_amount_decline';
  const [value, setValue] = useState(0);
  const [oldQuestionValueText, setOldQuestionValueText] = useState<any>(null);
  useEffect(() => {
    const { min, max, type, isInverted } = metadata as any;
    const avgValue = (min + max) / 2;
    if (isInvestmentAmountDecline) {
      const questionValue = questions.find(
        ({ attributeKey }) => attributeKey === 'investment_amount',
      );
      setOldQuestionValueText(questionnaireValue[questionValue.questionId]);
    }
    const currentQuestionValue = questionnaireValue[selectedQuestionId];
    if (isNotInvalid(currentQuestionValue)) {
      setValue(getOriginalValue(attributeKey, currentQuestionValue));
    } else {
      setValue(type === 'DISCRETE' ? avgValue : isInverted ? max : min);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey]);
  useEffect(() => {
    if (isNotInvalid(value)) {
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]:
          metadata.type === 'DISCRETE'
            ? value
            : getFormattedValue(attributeKey, value),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey, value]);
  if (metadata.type === 'DISCRETE') {
    const { min, max, discreteValues } = metadata as any;
    const avgValue = (min + max) / 2;

    return (
      <Wrapper>
        <StyledSlider
          valueLabelDisplay="off"
          components={{
            Thumb: AirbnbThumbComponent,
          }}
          marks={discreteValues}
          min={min}
          max={max}
          value={value}
          step={getDiscreteSteps(
            min,
            max,
            discreteValues.map(({ value }) => value),
          )}
          aria-label="custom thumb label"
          defaultValue={avgValue}
          onChangeCommitted={(_, val) => {
            setValue(+val);
          }}
          isDiscreteSlider
        />
      </Wrapper>
    );
  }
  const { min, max } = metadata as any;
  return (
    <Wrapper>
      <StyledSlider
        valueLabelDisplay="on"
        valueLabelFormat={x => renderLabel(attributeKey, x)}
        components={{
          Thumb: AirbnbThumbComponent,
        }}
        min={min}
        max={max}
        value={value}
        aria-label="custom thumb label"
        isInverted={metadata.isInverted}
        // defaultValue={min}
        // track=
        // track={min < 0 && max === 0 ? 'inverted' : 'normal'}
        onChange={(_, val) => {
          setValue(+val);
        }}
      />
      {isInvestmentAmountDecline && (
        <DynamicText>
          If this happens, your initial{' '}
          {'$' + currencyFormat(oldQuestionValueText)} may reach down to{' '}
          {'$' + currencyFormat((1 + value / 100) * oldQuestionValueText)}
        </DynamicText>
      )}
    </Wrapper>
  );
}

export default MainSlider;

// Investment portfolio size and time horizon are key factors for your appropriate investment risk.
